/**
 * `EvidenceAnswerPorts` — the ONE bundle the answer surface crosses on
 * (ruling 62), and the provider/hook that deliver it.
 *
 * THIRTEEN members: the four that already existed as
 * `OpenSearchBlockSourceActions`, plus the public-route predicate, the
 * authenticated-URL predicate, the OCR-quality tag slot, the partner-config
 * resolver, the source-actions component, the answer-event emitter, the CPD
 * read-marker and the document reprint. Bound once per composition point that
 * renders an answer surface;
 * a component reading a port with no provider above it throws and names the
 * port rather than rendering a degraded badge.
 *
 * THE FAVICON RESOLVER IS NOT ONE OF THEM (amendment 62c). Ruling 63 put
 * `getFaviconUrl` on `@heidi/utils`, a door this zone may import, so a port
 * bought nothing — and it cost: it was `PartnerLogo`'s ONLY member, which made
 * a logo in the settings tree, the mobile source drawer and the clinical-tools
 * submenu throw for want of a provider none of those paths has.
 *
 * IT LIVES IN `/ui`, NOT `/contracts`, and that is not a placement accident:
 * `ocrQualityTag` and `sourceActionsComponent` are `ComponentType`, and
 * `manifest-boundary.test.ts`'s contracts-inertness case refuses `react` by
 * name. Every react-free payload and vocabulary type named below is declared on
 * `/contracts` instead, which is the split ruling 24 ratifies.
 */

import {
  createContext,
  createElement,
  useContext,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react';
import type {
  EvidenceAnswerEvent,
  EvidenceSourceClickedEventProps,
  HtmlEmbedHint,
  IntegrationPartnerConfig,
  SimpleCitation,
  SourceCardActionsProps,
  ViewerSource,
} from '../../contracts';

export interface EvidenceAnswerPorts {
  /** 1 — open a source in the in-app viewer panel. `useSetAtom(viewingSourceAtom)`. */
  openInViewer: (source: ViewerSource) => void;
  /** 2 — open a library document in the in-app preview. `useSetAtom(documentPreviewAtom)`. */
  openLibraryDocument: (documentId: string) => void;
  /** 3 — open a URL in a new browser tab. `window.open`. */
  openInNewTab: (url: string) => void;
  /** 4 — the unified `evidence_source_clicked` emit. */
  trackSourceClick: (props: EvidenceSourceClickedEventProps) => void;
  /**
   * 5 — is the current route public? A THUNK, not a boolean: the bundle is
   * memoised at the composition point and the route changes under it.
   */
  isPublicRoute: () => boolean;
  /**
   * 6 — does this URL need an authenticated fetch (or inline HTML render)?
   * `isAuthenticatedSourceUrl`, which reads nine `NEXT_PUBLIC_*` values through
   * `getDomains()` and stays app-side by ruling 52.
   */
  isAuthenticatedUrl: (url: string, embed?: HtmlEmbedHint) => boolean;
  /**
   * 7 — the OCR-quality tag for a cited library document. ONE component slot
   * rather than a resolver plus a badge: `useCitationOcrQuality` is a HOOK
   * reading a feature flag, and a resolver port would have package render call
   * an app hook.
   */
  ocrQualityTag: ComponentType<{ citation: SimpleCitation }>;
  /** 8 — partner branding by id. Reads the query cache during render; never moves. */
  getPartnerConfig: (partnerId: string) => IntegrationPartnerConfig | undefined;
  /** 9 — the source action row. `SourceCardActions` and its feedback subgraph; never moves. */
  sourceActionsComponent: ComponentType<SourceCardActionsProps>;
  /** 10 — the five remaining emits, over the union declared on `/contracts`. */
  trackAnswerEvent: (event: EvidenceAnswerEvent) => void;
  /** 11 — mark a CPD source read by URL. CPD is uncarved, so it crosses. */
  markSourceRead: (url: string, title?: string) => void;
  /**
   * 12 — reprint a generated document. `triggerBrowserPrint` fetches the URL,
   * loads it into a hidden same-origin iframe and opens the native dialog; the
   * fetch and the allow-list it checks are app-side, so the port is one-way and
   * the block never sees the promise.
   */
  printDocument: (url: string, context?: { toolId?: string }) => void;
  /**
   * 13 — exchange an opaque partner-figure id for a short-lived authenticated
   * object URL. Storage coordinates never cross this port.
   */
  resolvePartnerFigureUrl: (figureId: string) => Promise<string>;
}

const EvidenceAnswerPortsContext = createContext<EvidenceAnswerPorts | null>(null);

export interface EvidenceAnswerPortsProviderProps {
  ports: EvidenceAnswerPorts;
  children: ReactNode;
}

/**
 * Mount once per composition point that renders an answer surface. Memoise
 * `ports` at the call site — every consumer re-renders when the identity
 * changes, which is why member 6 is a thunk rather than a resolved boolean.
 */
export const EvidenceAnswerPortsProvider = ({
  ports,
  children,
}: EvidenceAnswerPortsProviderProps): ReactElement =>
  createElement(EvidenceAnswerPortsContext.Provider, { value: ports }, children);

/**
 * Read the bundle. Throws when no provider is above the caller, naming the
 * missing composition point — a degraded render would ship a badge with no
 * analytics and no viewer routing, and look correct.
 */
export const useEvidenceAnswerPorts = (): EvidenceAnswerPorts => {
  const ports = useContext(EvidenceAnswerPortsContext);
  if (!ports) {
    throw new Error(
      'EvidenceAnswerPorts is missing: wrap this answer surface in <EvidenceAnswerPortsProvider>.'
    );
  }
  return ports;
};
